import {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';
import ChekinProSettings from 'ChekinProSettings';
import {DEVICE, useScreenResize} from '@chekin/guestapp-ui-kit';
import {usePath} from 'hooks';
import {PATHS} from 'routes/constants';
import {useSummary} from 'context/summary';
import {useKioskoStore} from 'context/kiosko/useKioskoStore';
import {useIVStore} from '@chekin/shared';
import {setIVIntentRemoteSession} from 'hooks/iv/useIVRemoteSync';
import {API, getUserIdFromToken} from '@chekin/api';
import {getFeatureFlags, QUERY_CACHE_KEYS} from '@chekin/core';

export const useRemoteIVFlow = (isNeedContactVerification?: boolean) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {reservationId} = useSummary();
  const {isMatch: isMobile} = useScreenResize(DEVICE.mobileXL);
  const isKiosko = useKioskoStore(state => state.connection.isConnected);
  const {getFullPath, getMainPath} = usePath();
  const {initIVState} = useIVStore();
  const isPropertyQRScanned = useIVStore(state => state.isPropertyQRScanned);
  const getStartVerificationPath = useCallback(
    (isContactVerification?: boolean) => {
      if (isContactVerification) return PATHS.verification.contacts;
      if (
        isMobile ||
        isKiosko ||
        ChekinProSettings.isSDK ||
        !getFeatureFlags('IVCO_49_MOVE_IV_TO_MOBILE')
      )
        return PATHS.verification.documentSelection;
      return PATHS.verification.scanQRCode;
    },
    [isMobile, isKiosko],
  );

  const goNextAfterContactVerification = useCallback(() => {
    const path = getStartVerificationPath(false);
    navigate(`../${path}`);
  }, [navigate, getStartVerificationPath]);

  const goToDocumentSelection = useCallback(
    () => navigate(`../${PATHS.verification.documentSelection}`),
    [navigate],
  );

  const goToGuestForm = useCallback(() => {
    navigate(getFullPath(PATHS.addPersonalDataForm));
  }, [navigate, getFullPath]);

  const goToGuestRegistration = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [QUERY_CACHE_KEYS.guestGroup],
    });
    navigate(getMainPath(PATHS.guestRegistration));
  }, [queryClient, navigate, getMainPath]);

  const goToHome = useCallback(() => {
    navigate(getFullPath(PATHS.home));
  }, [navigate, getFullPath]);

  const startVerification = useCallback(async () => {
    try {
      if (getFeatureFlags('IVCO_49_MOVE_IV_TO_MOBILE')) {
        const ivIntent = await API.identityVerification.createIvIntent({
          id: crypto?.randomUUID(),
          reservation_id: reservationId,
          user_id: getUserIdFromToken(),
          is_property_qr_scanned: isPropertyQRScanned,
        });
        setIVIntentRemoteSession(ivIntent.id);
      }
    } catch (error) {
      console.error(error);
    }

    initIVState();
    navigate(getStartVerificationPath(isNeedContactVerification));
  }, [
    getStartVerificationPath,
    navigate,
    reservationId,
    initIVState,
    isPropertyQRScanned,
    isNeedContactVerification,
  ]);

  return {
    startVerification,
    goToDocumentSelection,
    goToGuestForm,
    goToHome,
    goToGuestRegistration,
    goNextAfterContactVerification,
  };
};
